/**
 * Cruscotto — i quattro indicatori del wireframe §6 e le code di lavoro.
 *
 * I numeri non arrivano da un endpoint di riepilogo: sono calcolati in
 * `lib/cruscotto.ts` dalle stesse liste che l'operatore apre cliccando sulla
 * scheda. Cosi' il KPI e la pagina di dettaglio non possono divergere, che e' il
 * difetto tipico dei cruscotti alimentati da una query separata.
 */

import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { elencaPubblicazioni } from "../api/client";
import type { Pubblicazione, RegolaObbligo } from "../api/types";
import {
  ChipStato,
  ChipNodo,
  Avviso,
  Scheda,
  Scheletro,
  StatoVuoto,
} from "../components/primitive";
import { useAutenticazione } from "../auth/contesto";
import { useAlberatura } from "../dati/contesto-alberatura";
import { calcolaIndicatori, documentiConValidazioneFallita } from "../lib/cruscotto";
import { calcolaScadenze, scadenzeImminenti } from "../lib/scadenze";
import { catenePerId, partiPercorso, SEPARATORE_PERCORSO } from "../lib/alberatura";
import { useRegole } from "../lib/useRegole";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

/** Riferimento stabile per «nessuna regola», mentre le regole non sono arrivate. */
const NESSUNA_REGOLA: readonly RegolaObbligo[] = [];

/** Testo dei giorni residui, con il caso del ritardo esplicitato. */
function testoResidui(giorni: number): string {
  if (giorni < 0) return `in ritardo di ${Math.abs(giorni)} gg`;
  if (giorni === 0) return "scade oggi";
  return `fra ${giorni} gg`;
}

export function Cruscotto() {
  const {
    ente,
    nodi,
    inCaricamento: alberaturaInCorso,
    errore: erroreAlberatura,
  } = useAlberatura();
  const catene = catenePerId(nodi);
  const enteId = ente?.id ?? null;

  // Le pubblicazioni dell'ente che si sta guardando, come in «Scadenze e
  // allerte»: senza `ente`, al super admin arrivavano quelle di tutti gli
  // enti, abbinate ai nodi di questo per `id_nodo`.
  const carica = useCallback(
    (segnale: AbortSignal) =>
      enteId === null
        ? Promise.resolve([] as Pubblicazione[])
        : elencaPubblicazioni({ ente: enteId }, segnale),
    [enteId],
  );
  const { dato: pubblicazioni, inCaricamento, errore } = useRisorsa(carica, [enteId]);
  const { eSuperAdmin, nodiAssegnati } = useAutenticazione();

  // Le cadenze dichiarate dal Comune: senza, il KPI «In scadenza» contava con
  // l'ipotesi della sezione mentre «Scadenze e allerte», un clic piu' in la',
  // con la cadenza dichiarata. Se non arrivano si conta con le ipotesi, come prima:
  // il cruscotto non si ferma per un dato di contorno.
  const regole = useRegole(enteId).dato ?? NESSUNA_REGOLA;

  useEffect(() => {
    document.title = "Cruscotto · Amministrazione trasparente";
  }, []);

  const elenco = pubblicazioni ?? [];
  const inAttesa = inCaricamento || alberaturaInCorso;
  // Il perimetro vale anche per i numeri: un redattore con tre obblighi
  // assegnati leggeva «118 nodi senza contenuti», che sono quelli dell'ente e
  // non i suoi, e il collegamento lo portava a una pagina con tre righe.
  const indicatori = calcolaIndicatori(
    nodi,
    elenco,
    eSuperAdmin ? undefined : new Set(nodiAssegnati),
    regole,
  );
  const imminenti = scadenzeImminenti(calcolaScadenze(nodi, elenco, regole)).slice(0, 6);
  const fallite = documentiConValidazioneFallita(elenco).slice(0, 5);
  const recenti = [...elenco]
    .sort((prima, seconda) => seconda.id - prima.id)
    .slice(0, 8);

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Cruscotto"
        titolo="Stato della sezione"
        sottotitolo="Contenuti pubblicati, adempimenti in avvicinamento e documenti bloccati dalla pipeline di validazione."
        azioni={
          <Link className="bottone bottone--primario" to="/documenti">
            Carica un documento
          </Link>
        }
      />

      {erroreAlberatura ? (
        <Avviso tono="critico" titolo="Alberatura non disponibile">
          {erroreAlberatura.messaggioEsteso}
        </Avviso>
      ) : null}
      {errore ? (
        <Avviso tono="critico" titolo="Pubblicazioni non disponibili">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}

      {inAttesa ? (
        <div className="griglia-kpi">
          {[0, 1, 2, 3].map((indice) => (
            <div key={indice} className="scheda" style={{ padding: "1.1rem 1.25rem" }}>
              <Scheletro righe={3} />
            </div>
          ))}
        </div>
      ) : (
        <div className="griglia-kpi">
          {indicatori.map((indicatore) => (
            <Link
              key={indicatore.chiave}
              to={indicatore.collegamento}
              className={`kpi kpi--${indicatore.tono}`}
            >
              <div className="kpi__etichetta">{indicatore.etichetta}</div>
              <div className="kpi__valore">{indicatore.valore}</div>
              <div className="kpi__nota">{indicatore.nota}</div>
            </Link>
          ))}
        </div>
      )}

      {fallite.length > 0 ? (
        <Avviso tono="critico" titolo="Documenti bloccati dalla validazione">
          <ul className="pila-stretta" style={{ marginTop: "0.35rem" }}>
            {fallite.map(({ pubblicazione, documento }) => (
              <li key={`${pubblicazione.id}-${documento.id}`}>
                <strong>{documento.titolo}</strong> in «{pubblicazione.titolo}» —{" "}
                {documento.esiti_validazione?.controlli?.find(
                  (controllo) => controllo.esito === "fallito",
                )?.messaggio ?? "controllo non superato"}
              </li>
            ))}
          </ul>
          Un documento con validazione fallita non risulta pubblicato: va sostituito con una
          versione conforme.
        </Avviso>
      ) : null}

      <Scheda
        titolo="Adempimenti in avvicinamento"
        descrizione="Obblighi da aggiornare secondo la loro frequenza, documenti alla fine della pubblicazione e file da sostituire."
        azioni={
          <Link className="bottone bottone--contorno bottone--piccolo" to="/scadenzario">
            Vedi tutto
          </Link>
        }
        fitto
      >
        {inAttesa ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={4} />
          </div>
        ) : imminenti.length === 0 ? (
          <StatoVuoto
            titolo="Nessun adempimento in scadenza"
            testo="Nei prossimi trenta giorni non risultano obblighi da rinnovare."
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Nodo</th>
                  <th scope="col">Adempimento</th>
                  <th scope="col">Scadenza</th>
                </tr>
              </thead>
              <tbody>
                {imminenti.map((scadenza, indice) => (
                  <tr key={`${scadenza.id_nodo}-${scadenza.tipo}-${indice}`}>
                    <td className="tabella__principale">
                      {/* Percorso completo: fra i 219 nodi 52 denominazioni si
                          ripetono, e due righe identiche non dicono su quale
                          obbligo si sta per intervenire. */}
                      <ChipNodo
                        {...partiPercorso(catene, scadenza.id_nodo)}
                        separatore={SEPARATORE_PERCORSO}
                      />
                    </td>
                    <td className="testo-tenue">{scadenza.descrizione}</td>
                    <td className="tabella__numero">
                      <span className={scadenza.giorni_residui < 0 ? "campo__errore" : undefined}>
                        {testoResidui(scadenza.giorni_residui)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>

      <Scheda
        titolo="Ultime pubblicazioni"
        azioni={
          <Link className="bottone bottone--contorno bottone--piccolo" to="/pubblicazioni">
            Apri l'elenco
          </Link>
        }
        fitto
      >
        {inAttesa ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={5} />
          </div>
        ) : recenti.length === 0 ? (
          <StatoVuoto
            titolo="Nessuna pubblicazione"
            testo="Il primo contenuto nasce come bozza: si carica il file sull'obbligo e lo si pubblica dall'alberatura."
            azione={
              <Link className="bottone bottone--primario" to="/documenti">
                Carica un documento
              </Link>
            }
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Titolo</th>
                  <th scope="col">Nodo</th>
                  <th scope="col">Anno</th>
                  <th scope="col">Stato</th>
                  <th scope="col">Ultimo aggiornamento</th>
                </tr>
              </thead>
              <tbody>
                {recenti.map((pubblicazione) => (
                  <tr key={pubblicazione.id}>
                    <td className="tabella__principale">{pubblicazione.titolo}</td>
                    <td className="testo-tenue">{pubblicazione.nodo_id ?? "—"}</td>
                    <td className="tabella__numero">{pubblicazione.anno_riferimento}</td>
                    <td>
                      <ChipStato stato={pubblicazione.stato} />
                    </td>
                    <td className="dato testo-tenue">
                      {pubblicazione.data_ultimo_aggiornamento ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>
    </div>
  );
}
